import React, { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import config from "aldoo-ra/Config"
import { InformationCircleIcon, XMarkIcon, ClipboardIcon, CheckIcon, CommandLineIcon, LinkIcon } from "@heroicons/react/24/outline"

const INSTRUCTIONS = {
  STRIPE_PUBLISHABLE_KEY: {
    title: "How to get your Stripe Publishable Key",
    steps: [
      { step: 1, text: "Log in to your Stripe Dashboard at", link: { label: "dashboard.stripe.com", url: "https://dashboard.stripe.com" } },
      { step: 2, text: 'Go to Developers → API keys from the left sidebar' },
      { step: 3, text: 'Find the "Publishable key" — it starts with pk_live_ (production) or pk_test_ (test mode)' },
      { step: 4, text: 'Click "Reveal live key" or copy the test key directly' },
      { step: 5, text: 'Paste it here. Use pk_test_ keys during development, pk_live_ in production' },
    ],
    note: "The publishable key is safe to expose in client-side code. It only allows creating payment intents and tokens."
  },
  STRIPE_SECRET_KEY: {
    title: "How to get your Stripe Secret Key",
    steps: [
      { step: 1, text: "Log in to your Stripe Dashboard at", link: { label: "dashboard.stripe.com", url: "https://dashboard.stripe.com" } },
      { step: 2, text: 'Go to Developers → API keys from the left sidebar' },
      { step: 3, text: 'Find the "Secret key" section — it starts with sk_live_ (production) or sk_test_ (test mode)' },
      { step: 4, text: 'Click "Reveal live key" to display the key (you may need to re-authenticate)' },
      { step: 5, text: 'Copy and paste it here. Never share or commit this key' },
    ],
    note: "Keep this key strictly server-side. It grants full API access to your Stripe account."
  },
  STRIPE_WEBHOOK_SECRET: {
    title: "How to get your Stripe Webhook Secret",
    steps: [
      { step: 1, text: "Log in to your Stripe Dashboard at", link: { label: "dashboard.stripe.com", url: "https://dashboard.stripe.com" } },
      { step: 2, text: 'Go to Developers → Webhooks from the left sidebar' },
      { step: 3, text: 'Click "Add endpoint" and enter your server\'s webhook URL (e.g. https://yourdomain.com/api/stripe/webhook)' },
      { step: 4, text: 'Select the events to listen to (e.g. checkout.session.completed, invoice.paid)' },
      { step: 5, text: 'After creating the endpoint, click on it and find "Signing secret"' },
      { step: 6, text: 'Click "Reveal" to show the secret — it starts with whsec_' },
      { step: 7, text: 'Copy and paste it here' },
    ],
    note: "This secret is used to verify that webhook events were sent by Stripe and not a third party."
  }
}

// ─── Copy Button ────────────────────────────────────────────────────────────

const CopyButton = ({ text, className = "" }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy to clipboard"
      className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium transition-colors ${
        copied
          ? "bg-admin_success/20 text-admin_success border-admin_success/40"
          : "bg-admin_neutral_1 text-admin_text_secondary border-admin_bordered hover:text-admin_text hover:border-admin_primary"
      } ${className}`}
    >
      {copied
        ? <><CheckIcon className="w-3.5 h-3.5" />Copied</>
        : <><ClipboardIcon className="w-3.5 h-3.5" />Copy</>
      }
    </button>
  )
}

// ─── Webhook URL Helper ──────────────────────────────────────────────────────

const getWebhookInfo = () => {
  const apiUrl = (config.PLATFORM_API_URL || "").replace(/\/$/, "")
  const webhookPath = "/v1/stripe-webhook"
  const webhookUrl = `${apiUrl}${webhookPath}`

  const isLocal =
    apiUrl.includes("localhost") ||
    apiUrl.includes("127.0.0.1") ||
    apiUrl.includes("0.0.0.0")

  let port = "7055"
  try {
    const match = apiUrl.match(/:(\d+)/)
    if (match) port = match[1]
  } catch (_) {}

  const cliCommand = `stripe listen --forward-to localhost:${port}${webhookPath}`

  return { webhookUrl, isLocal, cliCommand }
}

const WebhookHelper = () => {
  const { webhookUrl, isLocal, cliCommand } = getWebhookInfo()

  return (
    <div className="mt-3 space-y-2">
      {/* Webhook URL row */}
      <div className="flex items-center gap-2 p-2.5 rounded-lg bg-admin_neutral_1 border border-admin_bordered">
        <LinkIcon className="w-3.5 h-3.5 text-admin_text_secondary flex-shrink-0" />
        <Typography variant="admin_txt2" className="text-admin_text_secondary mr-1 flex-shrink-0">
          Webhook URL:
        </Typography>
        <code className="flex-1 text-xs text-admin_text font-mono truncate">
          {webhookUrl}
        </code>
        <CopyButton text={webhookUrl} />
      </div>

      {isLocal ? (
        /* Local dev: show CLI forwarding command */
        <div className="rounded-lg border border-admin_bordered overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-admin_neutral_1 border-b border-admin_bordered">
            <CommandLineIcon className="w-3.5 h-3.5 text-admin_text_secondary" />
            <Typography variant="admin_txt2_dim" className="flex-1">
              Run this in your terminal to forward Stripe events locally:
            </Typography>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-[#0d0d0d]">
            <code className="flex-1 text-xs text-green-400 font-mono select-all">
              {cliCommand}
            </code>
            <CopyButton text={cliCommand} />
          </div>
          <div className="px-3 py-2 bg-admin_neutral_1 border-t border-admin_bordered">
            <Typography variant="admin_txt2_dim">
              The CLI will output a webhook signing secret — paste it into the field above.
            </Typography>
          </div>
        </div>
      ) : (
        /* Production/staging: show Stripe dashboard registration steps */
        <div className="rounded-lg border border-admin_bordered overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-admin_neutral_1 border-b border-admin_bordered">
            <InformationCircleIcon className="w-3.5 h-3.5 text-admin_text_secondary" />
            <Typography variant="admin_txt2_dim">
              Register this URL in your Stripe Dashboard:
            </Typography>
          </div>
          <div className="px-3 py-2.5 space-y-1.5 bg-admin_paper">
            {[
              "Go to Stripe Dashboard → Developers → Webhooks",
              'Click "Add endpoint"',
              "Paste the webhook URL above as the endpoint URL",
              "Select the events you want to receive (e.g. checkout.session.completed)",
              'After saving, click the endpoint → "Signing secret" → Reveal → paste it above',
            ].map((step, i) => (
              <div key={i} className="flex gap-2">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-admin_primary/80 text-white text-[10px] flex items-center justify-center font-semibold mt-0.5">
                  {i + 1}
                </span>
                <Typography variant="admin_txt2" className="text-admin_text leading-relaxed">
                  {step}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Instructions Panel ──────────────────────────────────────────────────────

const InstructionsPanel = ({ fieldId, onClose, anchorRef }) => {
  const panelRef = useRef(null)
  const instructions = INSTRUCTIONS[fieldId]
  const [position, setPosition] = useState({ top: 0, left: 0, width: 320 })

  useEffect(() => {
    if (!anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    const panelWidth = 360
    let left = rect.right + 8
    if (left + panelWidth > window.innerWidth - 16) {
      left = rect.left - panelWidth - 8
    }
    setPosition({
      top: rect.top,
      left: Math.max(8, left),
      width: panelWidth
    })
  }, [anchorRef])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose, anchorRef])

  if (!instructions) return null

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[9999] bg-admin_paper border border-admin_bordered rounded-lg shadow-xl overflow-hidden"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <div className="flex items-start justify-between p-4 border-b border-admin_bordered bg-admin_neutral_1">
        <Typography variant="admin_txt1" className="font-semibold text-admin_text pr-2 leading-snug">
          {instructions.title}
        </Typography>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-admin_text_secondary hover:text-admin_text transition-colors mt-0.5"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
        {instructions.steps.map(({ step, text, link }) => (
          <div key={step} className="flex gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-admin_primary text-white text-xs flex items-center justify-center font-semibold mt-0.5">
              {step}
            </span>
            <Typography variant="admin_txt2" className="text-admin_text leading-relaxed">
              {text}
              {link && (
                <>
                  {" "}
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-admin_primary underline hover:opacity-80"
                  >
                    {link.label}
                  </a>
                </>
              )}
            </Typography>
          </div>
        ))}

        {instructions.note && (
          <div className="mt-3 p-3 bg-admin_info_bg rounded-lg border border-admin_bordered">
            <Typography variant="admin_txt2_dim" className="leading-relaxed italic">
              {instructions.note}
            </Typography>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

// ─── Main Editor ─────────────────────────────────────────────────────────────

const StripeKeyEditor = ({
  onChange,
  value,
  field,
  readOnly = false,
  autoFocus = false,
}) => {
  const [localValue, setLocalValue] = useState(value || "")
  const [showInstructions, setShowInstructions] = useState(false)
  const buttonRef = useRef(null)

  useEffect(() => {
    setLocalValue(value || "")
  }, [value])

  const handleChange = (e) => {
    const newValue = e?.target?.value ?? e
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  const isWebhookSecret = field?.id === "STRIPE_WEBHOOK_SECRET"

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Input
            variant="admin"
            value={localValue}
            onChange={handleChange}
            readOnly={readOnly}
            autoFocus={autoFocus}
            placeholder={field?.placeholder || ""}
          />
        </div>

        <button
          ref={buttonRef}
          type="button"
          onClick={() => setShowInstructions((v) => !v)}
          className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium border transition-colors ${
            showInstructions
              ? "bg-admin_primary text-white border-admin_primary"
              : "bg-admin_neutral_1 text-admin_text_secondary border-admin_bordered hover:text-admin_text hover:border-admin_primary"
          }`}
        >
          <InformationCircleIcon className="w-3.5 h-3.5" />
          Instructions
        </button>

        {showInstructions && (
          <InstructionsPanel
            fieldId={field?.id}
            anchorRef={buttonRef}
            onClose={() => setShowInstructions(false)}
          />
        )}
      </div>

      {isWebhookSecret && <WebhookHelper />}
    </div>
  )
}

export default StripeKeyEditor
